import React, { useState } from "react";
import { Send, MessageCircle, Check, Plane } from "lucide-react";
import { useI18n } from "../i18n/LanguageContext";
import { createRequest, buildWhatsAppLink } from "../lib/api";
import { interp } from "../lib/destinations";
import { toast } from "sonner";

export const AviationForm = ({ destinationName }) => {
  const { t } = useI18n();
  const [done, setDone] = useState(false);
  const [waLink, setWaLink] = useState("#");
  const [submitting, setSubmitting] = useState(false);
  const [f, setF] = useState({
    trip: "oneway", from: "", to: destinationName || "", departDate: "", departTime: "",
    returnDate: "", passengers: "", luggage: "", aircraft: "", name: "", phone: "", email: "", notes: "",
  });
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const inputCls = "w-full bg-transparent border border-vm-border text-vm-text placeholder:text-vm-muted px-4 py-3 text-sm focus:border-vm-accent transition-colors duration-300 rounded-none";

  const submit = async (e) => {
    e.preventDefault();
    if (!f.name || !f.from || !f.to) {
      toast.error(t("request.required"));
      return;
    }
    setSubmitting(true);
    const tripLabel = t(`aviaForm.${f.trip}`);
    const message =
      `${tripLabel} — ${f.from} → ${f.to}. ${f.departDate} ${f.departTime}` +
      (f.returnDate ? ` / return ${f.returnDate}` : "") +
      `. ${f.passengers || "?"} pax, luggage: ${f.luggage || "-"}. Aircraft: ${f.aircraft || "-"}. ${f.notes || ""}`;
    const payload = {
      name: f.name, email: f.email, phone: f.phone, contact_method: "whatsapp",
      category: "aviation", urgency: "whenever", location: `${f.from} → ${f.to}`, date: f.departDate,
      message, source: "aviation_form",
      details: { trip: f.trip, from: f.from, to: f.to, departDate: f.departDate, departTime: f.departTime, returnDate: f.returnDate, passengers: f.passengers, luggage: f.luggage, aircraft: f.aircraft },
    };
    try {
      await createRequest(payload);
      setWaLink(buildWhatsAppLink({ ...payload, category: t("request.categories.aviation"), urgency: t("request.urgencies.whenever") }));
      setDone(true);
      toast.success(t("request.success"));
    } catch {
      toast.error("Error. Please try WhatsApp.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="border border-vm-border p-10 text-center" data-testid="aviation-form-success">
        <div className="mx-auto mb-6 w-14 h-14 border border-vm-accent flex items-center justify-center">
          <Check strokeWidth={1} size={26} className="text-vm-accent" />
        </div>
        <h3 className="font-serif text-3xl font-light mb-3">{t("request.success")}</h3>
        <p className="text-vm-text2 text-sm mb-8">{t("common.availability")}</p>
        <a href={waLink} target="_blank" rel="noopener noreferrer" className="vm-btn-primary inline-flex items-center gap-2 px-8 py-3 text-sm tracking-wide" data-testid="aviation-continue-whatsapp">
          <MessageCircle strokeWidth={1.5} size={18} /> {t("request.openWhatsApp")}
        </a>
      </div>
    );
  }

  const TRIPS = ["oneway", "return", "multicity"];

  return (
    <form onSubmit={submit} className="border border-vm-border p-8 md:p-12" data-testid="aviation-form">
      <div className="flex items-center gap-3 mb-2">
        <Plane strokeWidth={1.25} size={18} className="text-vm-accent vm-flip" />
        <p className="vm-label">{t("aviation.label")}</p>
      </div>
      <h3 className="font-serif text-3xl md:text-4xl font-light mb-2">{interp(t("aviaForm.title"), destinationName)}</h3>
      <p className="text-vm-text2 text-sm mb-8">{t("aviaForm.proposal")}</p>

      <div className="flex flex-wrap gap-2 mb-6">
        {TRIPS.map((tr) => (
          <button type="button" key={tr} onClick={() => set("trip", tr)}
            className={`px-5 py-2 text-xs tracking-[0.15em] uppercase border transition-colors duration-300 ${f.trip === tr ? "border-vm-accent text-vm-accent" : "border-vm-border text-vm-text2 hover:text-vm-text"}`}
            data-testid={`aviation-trip-${tr}`}>
            {t(`aviaForm.${tr}`)}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <input value={f.from} onChange={(e) => set("from", e.target.value)} placeholder={t("aviaForm.from")} className={inputCls} data-testid="aviation-from" />
        <input value={f.to} onChange={(e) => set("to", e.target.value)} placeholder={t("aviaForm.to")} className={inputCls} data-testid="aviation-to" />
        <input type="date" value={f.departDate} onChange={(e) => set("departDate", e.target.value)} className={inputCls} data-testid="aviation-depart-date" aria-label={t("aviaForm.departDate")} />
        <input type="time" value={f.departTime} onChange={(e) => set("departTime", e.target.value)} className={inputCls} data-testid="aviation-depart-time" aria-label={t("aviaForm.departTime")} />
        {f.trip !== "oneway" && (
          <input type="date" value={f.returnDate} onChange={(e) => set("returnDate", e.target.value)} className={inputCls} data-testid="aviation-return-date" aria-label={t("aviaForm.returnDate")} />
        )}
        <input type="number" min="1" value={f.passengers} onChange={(e) => set("passengers", e.target.value)} placeholder={t("aviaForm.passengers")} className={inputCls} data-testid="aviation-passengers" />
        <input value={f.luggage} onChange={(e) => set("luggage", e.target.value)} placeholder={t("aviaForm.luggage")} className={inputCls} data-testid="aviation-luggage" />
        <input value={f.aircraft} onChange={(e) => set("aircraft", e.target.value)} placeholder={t("aviaForm.aircraft")} className={inputCls} data-testid="aviation-aircraft" />
        <input value={f.name} onChange={(e) => set("name", e.target.value)} placeholder={t("common.name")} className={inputCls} data-testid="aviation-name" />
        <input value={f.phone} onChange={(e) => set("phone", e.target.value)} placeholder={t("common.phone")} className={inputCls} data-testid="aviation-phone" />
        <input value={f.email} onChange={(e) => set("email", e.target.value)} placeholder={t("common.emailLabel")} className={inputCls} data-testid="aviation-email" />
      </div>

      <textarea value={f.notes} onChange={(e) => set("notes", e.target.value)} placeholder={t("aviaForm.notes")} rows={3} className={inputCls + " resize-none mb-6"} data-testid="aviation-notes" />

      <button type="submit" disabled={submitting} className="vm-btn-primary w-full inline-flex items-center justify-center gap-2 py-4 text-xs tracking-widest uppercase disabled:opacity-50" data-testid="aviation-submit">
        <Send strokeWidth={1.5} size={16} /> {submitting ? t("request.sending") : t("aviaForm.submit")}
      </button>
    </form>
  );
};
